/**
 * @module hooks/useConnectivity
 * @description React hook that exposes the current network connectivity status
 * and pending offline transaction count. Initializes the connectivity service
 * and sync service on first use.
 *
 * @example
 * const { isOnline, pendingCount, forceCheck } = useConnectivity();
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  initConnectivity,
  onStatusChange,
  getStatus,
  forceCheck as serviceForceCheck,
} from '../services/connectivityService';
import { initSync, onSyncEvent } from '../services/offlineSync';
import { getPendingSummary } from '../services/offlineDb';

/**
 * @typedef {Object} ConnectivityState
 * @property {boolean} isOnline - Whether the API is reachable
 * @property {'online'|'offline'} status - Current connectivity status string
 * @property {number} pendingCount - Number of offline transactions waiting to sync
 * @property {{orders: number, payments: number, updates: number}} pendingSummary
 * @property {boolean} isSyncing - Whether a sync is currently in progress
 * @property {Function} forceCheck - Trigger an immediate connectivity check
 * @property {Function} refreshPending - Refresh the pending count
 */

/**
 * React hook for connectivity awareness.
 * Initializes the connectivity monitoring and offline sync services.
 * @returns {ConnectivityState}
 */
export function useConnectivity() {
  const [status, setStatus] = useState(getStatus());
  const [pendingSummary, setPendingSummary] = useState({
    orders: 0,
    payments: 0,
    updates: 0,
    total: 0,
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const initializedRef = useRef(false);

  const refreshPending = useCallback(async () => {
    try {
      const summary = await getPendingSummary();
      setPendingSummary(summary);
    } catch {
      // IndexedDB may not be available in some test environments
    }
  }, []);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Initialize connectivity monitoring
    initConnectivity().then((initialStatus) => {
      setStatus(initialStatus);
    });

    // Initialize sync service
    initSync();

    // Refresh pending count on load
    refreshPending();
  }, [refreshPending]);

  useEffect(() => {
    // Subscribe to connectivity changes
    const unsubConnectivity = onStatusChange((newStatus) => {
      setStatus(newStatus);
      // Refresh pending count when status changes
      refreshPending();
    });

    // Subscribe to sync events
    const unsubSync = onSyncEvent((event) => {
      if (event.type === 'sync_start') {
        setIsSyncing(true);
      } else if (
        event.type === 'sync_complete' ||
        event.type === 'sync_error'
      ) {
        setIsSyncing(false);
        refreshPending();
      } else if (event.type === 'sync_progress') {
        refreshPending();
      }
    });

    return () => {
      unsubConnectivity();
      unsubSync();
    };
  }, [refreshPending]);

  const forceCheck = useCallback(async () => {
    const result = await serviceForceCheck();
    setStatus(result);
    return result;
  }, []);

  return {
    isOnline: status === 'online',
    status,
    pendingCount: pendingSummary.total,
    pendingSummary,
    isSyncing,
    forceCheck,
    refreshPending,
  };
}

export default useConnectivity;
